import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Sparkles, Star, ThumbsUp, TrendingUp } from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { FACES, SOCIAL_PROOF_NAMES } from "../lib/marketing-data";
import { AnimatedNumber } from "../ui/AnimatedNumber";
import { Avatar } from "../ui/primitives";
import { PromoTour, TourThumb } from "../ui/PromoTour";

/**
 * Full-screen split auth layout (ported from the v2 demo). The marketing panel
 * only renders on the Hyperclapper domain — white-label/reseller domains get a
 * plain single-column form (same gating the old LoginLeftPanel had).
 */
export function AuthShell({ eyebrow, title, subtitle, children, footer, proof }) {
  const { t } = useTranslation();
  const { matchedDomain } = useContext(AuthContext);
  const { theme } = useTheme();
  const isHyper = matchedDomain?.name === "Hyperclapper";

  return (
    <div
      className={
        isHyper
          ? "fixed inset-0 z-[80] grid grid-cols-1 grid-rows-1 overflow-hidden bg-[var(--background-2)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"
          : "fixed inset-0 z-[80] grid grid-cols-1 grid-rows-1 overflow-hidden bg-[var(--background-2)]"
      }
    >
      {/* Left — form column; scrolls if the form is taller than the viewport */}
      <div className="overflow-y-auto [scrollbar-gutter:stable]">
        <div className="flex min-h-full flex-col px-6 py-7 sm:px-10 lg:px-14">
          <Link to="/" className="flex items-center gap-2.5">
            {isHyper ? (
              <>
                <span className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <span className="absolute inset-0 rounded-full bg-white" />
                <img src="/logo.svg" alt="HyperClapper" className="relative h-9 w-9 scale-[1.18]" />
              </span>
                <span className="text-[15px] font-semibold tracking-tight">
                  Hyper<span className="text-[var(--accent-bright)]">Clapper</span>
                </span>
              </>
            ) : (
              <>
                <img
                  src={theme === "light" ? matchedDomain?.logo : matchedDomain?.logo_dark}
                  alt={`${matchedDomain?.name || matchedDomain?.app_name || ""} logo`}
                  className="h-8 w-auto object-contain"
                />
                <span className="text-[15px] font-semibold tracking-tight">
                  {matchedDomain?.app_name}
                </span>
              </>
            )}
          </Link>

          <div className="flex flex-1 items-center py-8">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto w-full max-w-[400px]"
            >
              {eyebrow && (
                <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-soft)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--accent-bright)]">
                  {eyebrow}
                </span>
              )}
              <h1 className="text-[28px] font-semibold leading-tight tracking-tight">{title}</h1>
              <p className="mt-2 text-[14px] leading-relaxed text-muted">{subtitle}</p>
              <div className="mt-7">{children}</div>
              <div className="mt-7 text-[13.5px] text-muted">{footer}</div>
              {/* Optional social-proof band under the footer (the signup page's
                  "Trusted by" avatars, template 4f5de88). */}
              {proof && <div className="mt-8 border-t border-[var(--border)] pt-7">{proof}</div>}
            </motion.div>
          </div>

          <p className="text-[12px] text-muted-2">
            © {new Date().getFullYear()}{" "}
            {matchedDomain?.app_name || "HyperClapper"} ·{" "}
            {t("auth.marketing.builtFor", "Built for LinkedIn creators")}
          </p>
        </div>
      </div>

      {/* Right — marketing / social proof (Hyperclapper only, fixed height, no scroll) */}
      {isHyper && <AuthMarketing />}
    </div>
  );
}

function AuthMarketing() {
  const { t } = useTranslation();
  const [on, setOn] = useState(false);
  const [touched, setTouched] = useState(false);

  // On landing: show "on your own" (red) briefly, then auto-flip to HyperClapper.
  useEffect(() => {
    if (touched) return;
    const t = setTimeout(() => setOn(true), 2400);
    return () => clearTimeout(t);
  }, [touched]);

  const s = on
    ? { impressions: 36329, comments: 1987, likedBy: t("auth.marketing.likedByOn", "Benoit and 1,322 others") }
    : { impressions: 1, comments: 0, likedBy: t("auth.marketing.likedByOff", "Bertrand Dvs liked") };

  return (
    <div className="relative hidden h-full overflow-hidden lg:block">
      {/* branded backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(155deg, color-mix(in oklab, var(--accent) 90%, black), color-mix(in oklab, var(--accent) 55%, var(--chart-5)))",
        }}
      />
      <div className="grid-fade absolute inset-0 opacity-40" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(60rem 40rem at 80% -10%, rgba(255,255,255,0.18), transparent 60%)" }}
      />

      <div className="relative flex h-full flex-col justify-center px-14 py-12 text-white">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide backdrop-blur">
          {t("auth.marketing.eyebrow", "The growth engine for LinkedIn")}
        </span>
        <h2 className="mt-5 text-[32px] font-semibold leading-[1.14] tracking-tight">
          {t("auth.marketing.headline", "Building a LinkedIn brand")}
          <span className="mt-1.5 flex items-center gap-3">
            <span className="relative inline-flex min-w-[204px]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={on ? "on" : "off"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  {on
                    ? t("auth.marketing.withUs", "with HyperClapper")
                    : t("auth.marketing.onYourOwn", "on your own")}
                </motion.span>
              </AnimatePresence>
            </span>
            <button
              onClick={() => {
                setTouched(true);
                setOn((v) => !v);
              }}
              aria-label={t("auth.marketing.compareAria", "Compare with and without HyperClapper")}
              className="relative h-9 w-[60px] shrink-0 rounded-full transition-colors"
              style={{
                background: on
                  ? "color-mix(in oklab, var(--success) 34%, white)"
                  : "color-mix(in oklab, var(--danger) 34%, white)",
              }}
            >
              <motion.span
                animate={{ x: on ? 26 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className="absolute left-1 top-1 h-7 w-7 rounded-full shadow-md"
                style={{ background: on ? "var(--success)" : "var(--danger)" }}
              />
            </button>
          </span>
        </h2>
        <p className="mt-3 text-[13px] text-white/70">{t("auth.marketing.tapToggle", "Tap the toggle to see the difference.")}</p>

        {/* Morphing post card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="relative mt-7 max-w-[400px] rounded-2xl bg-white p-4 text-[var(--eclipse)] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]"
        >
          <div className="flex items-center gap-3">
            <Avatar label="RI" src={FACES[1]} size={44} />
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-zinc-900">{t("auth.marketing.demoName", "Roxana Irimia · You")}</p>
              <p className="truncate text-[12px] text-zinc-500">{t("auth.marketing.demoHeadline", "Head of Marketing · Building in public")}</p>
              <p className="text-[11px] text-zinc-400">{t("auth.marketing.demoTime", "4h")} · 🌐</p>
            </div>
          </div>
          <p className="mt-3 text-[13.5px] leading-relaxed text-zinc-800">
            {t(
              "auth.marketing.demoPost",
              "This morning, my one-person business crossed $3M in sales. It took 1,187 days, zero ads, 94% margin. Here are the 6 revenue streams I built 👇",
            )}
          </p>
          <div className="mt-3 flex items-center justify-between text-[12px] text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#0A66C2]">
                <ThumbsUp className="h-2.5 w-2.5 text-white" fill="white" strokeWidth={0} />
              </span>
              {s.likedBy}
            </span>
            <span className="tabular">
              {t("auth.marketing.commentsCount", {
                count: s.comments,
                formatted: s.comments.toLocaleString(),
                defaultValue: "{{formatted}} comments",
              })}
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-3">
            <AnimatePresence mode="wait">
              {on ? (
                <motion.div
                  key="on"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex -space-x-2"
                >
                  {[
                    { f: FACES[4], b: "#0A66C2", I: ThumbsUp },
                    { f: FACES[5], b: "var(--danger)", I: Heart },
                    { f: FACES[6], b: "var(--success)", I: Sparkles },
                    { f: FACES[7], b: "#8b5cf6", I: Heart },
                  ].map((r, i) => (
                    <span key={i} className="relative">
                      <Avatar label="" src={r.f} size={28} className="ring-2 ring-white" />
                      <span
                        className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ring-white"
                        style={{ background: r.b }}
                      >
                        <r.I className="h-2 w-2 text-white" fill="white" strokeWidth={0} />
                      </span>
                    </span>
                  ))}
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-100 text-[10px] font-semibold text-zinc-500 ring-2 ring-white">
                    …
                  </span>
                </motion.div>
              ) : (
                <motion.div
                  key="off"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <span className="relative">
                    <Avatar label="" src={FACES[10]} size={28} className="ring-2 ring-white" />
                    <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#0A66C2] ring-2 ring-white">
                      <ThumbsUp className="h-2 w-2 text-white" fill="white" strokeWidth={0} />
                    </span>
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-2.5 py-1.5 text-[12px] font-semibold text-zinc-700">
              <TrendingUp className="h-3.5 w-3.5" />
              <AnimatedNumber
                key={s.impressions}
                value={s.impressions}
                format={(n) => Math.round(n).toLocaleString()}
                duration={0.9}
              />{" "}
              {t("auth.marketing.impressionsWord", { count: s.impressions, defaultValue: "impressions" })}
            </span>
          </div>
        </motion.div>

        {/* Impact stats */}
        <div className="mt-9 grid grid-cols-3 gap-4 border-t border-white/15 pt-6">
          {[
            { v: "350K+", l: t("auth.marketing.postsBoosted", "Posts boosted") },
            { v: "30M+", l: t("auth.marketing.engagementsGenerated", "Engagements generated") },
            { v: "8,027+", l: t("auth.marketing.activeProfessionals", "Active professionals") },
          ].map((m) => (
            <div key={m.l}>
              <p className="text-[24px] font-bold leading-none tracking-tight">{m.v}</p>
              <p className="mt-1.5 text-[11.5px] leading-snug text-white/65">{m.l}</p>
            </div>
          ))}
        </div>

        {/* Social proof */}
        <div className="mt-7 flex items-center gap-4">
          <div className="flex shrink-0 -space-x-2.5">
            {SOCIAL_PROOF_NAMES.map((name, i) => (
              <Avatar
                key={i}
                label={name.slice(0, 2)}
                src={FACES[(i * 3) % FACES.length]}
                size={36}
                className="ring-2 ring-white/60"
              />
            ))}
          </div>
          <div>
            <div className="flex items-center gap-0.5 text-[var(--warning)]">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <p className="text-[12.5px] text-white/85">
              {t(
                "auth.marketing.lovedBy",
                "Loved by founders & creators worldwide",
              )}
            </p>
          </div>

          {/* The 45-second tour (template d28b178): the row only has room for
              the label on wide screens; the play disc carries it below that. */}
          <PromoTour>
            {(open) => (
              <button
                onClick={open}
                aria-label={t("tour.watch", "Watch the 45-second tour")}
                className="group ml-auto flex shrink-0 items-center gap-2.5 rounded-xl bg-white/12 p-1.5 text-left backdrop-blur transition-colors hover:bg-white/20 min-[1440px]:pr-3.5"
              >
                <TourThumb className="w-[76px] shrink-0 ring-1 ring-white/25" />
                <span className="hidden whitespace-nowrap text-[12.5px] font-semibold min-[1440px]:block">
                  {t("tour.short", "45-second tour")}
                </span>
              </button>
            )}
          </PromoTour>
        </div>
      </div>
    </div>
  );
}
